/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { ContactFinderListItem } from '@gh/builder-shared';
import { ContactDeliveryMethod, ScheduleSupervisorSupplierListItemDto } from '@gh/builder-shared-data';
import {
  createArrayHelper,
  entityComplementor,
  EntityConfig,
  entityValidatorByEntityConfig,
  fieldStateByEntityConfig,
  TextTypes,
} from '@gh/common-shared';
import { EMPTY_ID, ID, WithEntityState } from '@gh/common-shared-data';

import { eq, map, omit } from 'lodash';

export type SchedulingSupplierListItem =
  WithEntityState<ScheduleSupervisorSupplierListItemDto> & { supplierAddress?: string };

export const SCHEDULING_SUPPLIER_LIST_INIT: SchedulingSupplierListDetails = {
  supervisor: {
    userName: '',
    userId: EMPTY_ID,
  },
  suppliers: [],
  originalSuppliers: [],
};

export type SchedulingSupplierListDetails = {
  supervisor: Maybe<SchedulingSupervisorAutocomplete>;
  suppliers: SchedulingSupplierListItem[];
  originalSuppliers: SchedulingSupplierListItem[];
};

export type SchedulingSupplierListDetailsUpdate = {
  supervisor: SchedulingSupervisorAutocomplete;
  suppliers: SchedulingSupplierListItem[];
};

export type SchedulingSupervisorAutocomplete = {
  userName: string;
  userId: ID;
};

export function createFromContactFinderList({ id, name, email, address, scheduleBy }: ContactFinderListItem): SchedulingSupplierListItem {
  return complementSchedulingSupplier({
    id: void 0,
    supplierContactId: id,
    name,
    sendBy: scheduleBy || ContactDeliveryMethod.Email,
    isDeleted: false,
    email,
    address,
    supplierAddress: scheduleBy === ContactDeliveryMethod.Mail ? address : email,
  });
}

export const getSchedulingSupplierListItemId = (({ supplierContactId }: SchedulingSupplierListItem) => supplierContactId);
export const supervisorSuppliersArr = createArrayHelper(getSchedulingSupplierListItemId);

export const SCHEDULE_SUPERVISOR_SUPPLIERS_ENTITY_CONFIG = {
  sendBy: { editable: true, required: true },
  supplierAddress: { editable: false },
};
export const schedulingSupplierListFieldState = fieldStateByEntityConfig(SCHEDULE_SUPERVISOR_SUPPLIERS_ENTITY_CONFIG);

export const isSameSuppliers = (supplier: SchedulingSupplierListItem) =>
  (otherSupplier: SchedulingSupplierListItem): boolean =>
    supplier.supplierContactId === otherSupplier.supplierContactId;

export const supervisorSuppliersFromDto = (supplierList: ScheduleSupervisorSupplierListItemDto[]): SchedulingSupplierListItem[] =>
  map(supplierList, (supplier: ScheduleSupervisorSupplierListItemDto) => ({
    ...complementSchedulingSupplier({
      ...supplier,
      supplierAddress: supplier.sendBy === ContactDeliveryMethod.Email ? supplier.email : supplier.address,
    }),
  }));

export const supervisorSuppliersToDto = (supplierList: SchedulingSupplierListItem[]): ScheduleSupervisorSupplierListItemDto[] =>
  map(supplierList, (supplier: SchedulingSupplierListItem) =>
    <ScheduleSupervisorSupplierListItemDto>omit(supplier, ['entityState', 'supplierAddress']));

const SUPPLIER_ENTITY_CONFIG: EntityConfig<SchedulingSupplierListItem> = {
  supplierAddress: {
    type: ({ sendBy }: SchedulingSupplierListItem) =>
      eq(sendBy, ContactDeliveryMethod.Email) ? TextTypes.Email : TextTypes.Text255,
  },
};
export const validateSchedulingSupplier = entityValidatorByEntityConfig(SUPPLIER_ENTITY_CONFIG);
export const complementSchedulingSupplier = entityComplementor(validateSchedulingSupplier);
